import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, CalendarPlus } from 'lucide-react';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function SlotActionMenu({
  x,
  y,
  onAppointment,
  onBlock,
  onClose,
}: {
  x: number;
  y: number;
  onAppointment: () => void;
  onBlock: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onPointer(event: MouseEvent) {
      if (ref.current?.contains(event.target as Node)) return;
      onClose();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const left = Math.max(8, Math.min(x, window.innerWidth - 220));
  const top = Math.max(8, Math.min(y, window.innerHeight - 120));

  return (
    <div
      ref={ref}
      role="menu"
      data-testid="slot-action-menu"
      className="fixed z-50 w-[200px] rounded-lg border border-ds-line bg-ds-card p-s2 shadow-card"
      style={{ left, top }}
    >
      <p className={cn(TYPE.small, 'px-s2 pb-s1 text-ds-muted')}>
        {t('appointments.slot_menu.title')}
      </p>
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-s2 rounded-md px-s2 py-s2 text-left text-ds-text hover:bg-ds-sunk"
        onClick={onAppointment}
      >
        <CalendarPlus className="h-4 w-4 shrink-0" aria-hidden />
        {t('appointments.slot_menu.appointment')}
      </button>
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-s2 rounded-md px-s2 py-s2 text-left text-ds-text hover:bg-ds-sunk"
        onClick={onBlock}
      >
        <Ban className="h-4 w-4 shrink-0" aria-hidden />
        {t('appointments.slot_menu.block')}
      </button>
    </div>
  );
}
